import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Row, Col, Spin, Typography } from 'antd';
import {
    UserOutlined,
    HeartOutlined,
    LockOutlined,
    ArrowRightOutlined,
    WomanOutlined,
    ManOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import LandingNavbar from '../layouts/Navbar';
import LandingFooter from '../layouts/Footer';
import SearchCard from '../components/SearchCard';
import SEO from '../components/SEO';
import api from '../api/axios';

const { Title, Text } = Typography;

/* ── Derive a human-readable page title from URL params ── */
const buildTitle = (params, gender) => {
    const pronoun = gender === 'Female' ? 'Brides' : 'Grooms';
    const community  = params.get('community');
    const education  = params.get('education');
    const city       = params.get('city');
    const occupation = params.get('occupation');

    const slugToLabel = (s) =>
        s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    if (community)  return `${slugToLabel(community)} ${pronoun}`;
    if (occupation) return `${slugToLabel(occupation)} ${pronoun}`;
    if (education)  return `${slugToLabel(education)} ${pronoun}`;
    if (city)       return `${pronoun} from ${slugToLabel(city)}`;
    return `Latest ${pronoun}`;
};

/* ── Login wall ── */
const LoginWall = ({ gender }) => {
    const pronoun = gender === 'Female' ? 'brides' : 'grooms';
    return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 16px 0' }}>
            <div style={{
                textAlign: 'center',
                background: 'white',
                borderRadius: '20px',
                padding: '36px 40px',
                boxShadow: '0 20px 60px rgba(173,20,87,0.15)',
                border: '1px solid rgba(173,20,87,0.12)',
                maxWidth: '420px',
                width: '100%',
            }}>
                <div style={{
                    width: '56px', height: '56px', borderRadius: '16px',
                    background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 16px',
                    boxShadow: '0 8px 24px rgba(173,20,87,0.3)',
                }}>
                    <LockOutlined style={{ color: 'white', fontSize: '1.4rem' }} />
                </div>
                <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#1A1A2E', marginBottom: '8px', fontFamily: 'Calibri, sans-serif' }}>
                    See More {pronoun.charAt(0).toUpperCase() + pronoun.slice(1)}
                </div>
                <div style={{ color: '#64748B', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: '24px', fontFamily: 'Calibri, sans-serif' }}>
                    Create a free account to browse all profiles, send interest, and connect with your perfect match.
                </div>
                <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                    <Link to="/signup" style={{ textDecoration: 'none', display: 'block' }}>
                        <div style={{
                            height: '46px',
                            background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                            borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '8px',
                            color: 'white', fontWeight: 700, fontSize: '0.95rem',
                            fontFamily: 'Calibri, sans-serif',
                            cursor: 'pointer',
                            boxShadow: '0 6px 20px rgba(173,20,87,0.35)',
                            transition: 'all 0.2s ease',
                        }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 10px 28px rgba(173,20,87,0.45)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(173,20,87,0.35)';
                            }}
                        >
                            <HeartOutlined /> Create Free Account
                        </div>
                    </Link>
                    <Link to="/login" style={{ textDecoration: 'none', display: 'block' }}>
                        <div style={{
                            height: '46px',
                            border: '1.5px solid rgba(173,20,87,0.3)',
                            borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '8px',
                            color: '#AD1457', fontWeight: 600, fontSize: '0.95rem',
                            fontFamily: 'Calibri, sans-serif',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = '#AD1457';
                                e.currentTarget.style.background = 'rgba(173,20,87,0.04)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = 'rgba(173,20,87,0.3)';
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <UserOutlined /> Sign In <ArrowRightOutlined style={{ fontSize: '0.8rem' }} />
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

/* ── Main page ── */
const BrowseProfiles = () => {
    const [searchParams] = useSearchParams();
    const [gender, setGender]   = useState('Male');
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const isMobile = windowWidth < 768;

    useEffect(() => {
        const onResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const fetchProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                gender,
                community:  searchParams.get('community')  || undefined,
                education:  searchParams.get('education')  || undefined,
                city:       searchParams.get('city')       || undefined,
                occupation: searchParams.get('occupation') || undefined,
            };
            // strip undefined keys
            Object.keys(params).forEach(k => params[k] === undefined && delete params[k]);

            const res = await api.get('/browse/profiles', { params });
            setProfiles(res.data?.data ?? []);
        } catch {
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    }, [gender, searchParams]);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    const title    = buildTitle(searchParams, gender);
    const pronoun  = gender === 'Female' ? 'Brides' : 'Grooms';

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', minHeight: '100vh', background: '#FAFAFE' }}>
            <SEO
                description={`Browse ${title} on Oottan. Find verified matrimony profiles.`}
            />
            <LandingNavbar />

            <div>
                {/* ── Page header ── */}
                <div style={{
                    background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                    padding: isMobile ? `${72 + 32}px 20px 32px` : `${80 + 48}px 60px 48px`,
                }}>
                    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                        <Title style={{
                            color: 'white', margin: 0,
                            fontSize: isMobile ? '1.7rem' : '2.2rem',
                            fontWeight: 800,
                        }}>
                            {title}
                        </Title>
                        <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.97rem', display: 'block', marginTop: '6px' }}>
                            Showing the latest 5 profiles · Sign in to see all matches
                        </Text>

                        {/* ── Male / Female toggle ── */}
                        <div style={{
                            display: 'inline-flex',
                            background: 'rgba(255,255,255,0.15)',
                            borderRadius: '12px',
                            padding: '4px',
                            marginTop: '20px',
                            gap: '4px',
                        }}>
                            {['Male', 'Female'].map(g => (
                                <button
                                    key={g}
                                    onClick={() => setGender(g)}
                                    style={{
                                        padding: '8px 24px',
                                        borderRadius: '9px',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontFamily: 'Calibri, sans-serif',
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s ease',
                                        background: gender === g ? 'white' : 'transparent',
                                        color:      gender === g ? '#AD1457' : 'rgba(255,255,255,0.85)',
                                        boxShadow:  gender === g ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                                    }}
                                >
                                    {g === 'Male'
                                        ? <ManOutlined style={{ fontSize: '0.9rem' }} />
                                        : <WomanOutlined style={{ fontSize: '0.9rem' }} />}
                                    {g === 'Male' ? 'Grooms' : 'Brides'}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Profile grid ── */}
                <div style={{ padding: isMobile ? '32px 16px 80px' : '48px 60px 100px' }}>
                    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0', minHeight: '300px' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
                            </div>
                        ) : profiles.length === 0 ? (
                            <div style={{
                                textAlign: 'center', padding: '80px 20px',
                                background: 'white', borderRadius: '20px',
                                border: '1px solid rgba(173,20,87,0.08)',
                            }}>
                                <HeartOutlined style={{ fontSize: '2.5rem', color: '#D1D5DB', marginBottom: '16px', display: 'block' }} />
                                <div style={{ fontWeight: 700, color: '#1A1A2E', fontSize: '1.1rem', marginBottom: '6px' }}>
                                    No {pronoun} found
                                </div>
                                <Text style={{ color: '#64748B' }}>
                                    Try a different filter or check back later.
                                </Text>
                            </div>
                        ) : (
                            <>
                                <Row gutter={[20, 20]}>
                                    {profiles.map(profile => (
                                        <Col xs={24} sm={12} lg={8} key={profile.profile_id}>
                                            <SearchCard profile={profile} />
                                        </Col>
                                    ))}
                                </Row>

                                {/* Login wall — always shown below the 10 results */}
                                <LoginWall gender={gender} />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
};

export default BrowseProfiles;
